<template>
    <b-container fluid="">
        <b-card title="Doctor Queue">
            <b-row>
                <b-col>
                    <b-button variant="outline-secondary"
                              @click="doctorQueueTable.refresh()">
                        Refresh
                    </b-button>
                </b-col>
                <b-col>
                    <b-button variant="outline-info"
                              @click="selectAll">
                        Select All
                    </b-button>
                </b-col>
                <b-col>
                    <b-button variant="success"
                              :disabled="disableSubmit"
                              @click="submitScripts">
                        Submit
                    </b-button>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-table ref="doctorQueueTable"
                             :items="itemProvider"
                             :fields="fields"
                             api-url="api/DoctorQueue"
                             :tbody-tr-class="colorCodeTable"
                             sort-icon-left>
                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle"></b-spinner>
                                <strong>Loading...</strong>
                            </div>
                        </template>
                        <template #[`cell(selected)`]="data">
                            <input v-model="selected" type="checkbox" :value="data.item"/>
                        </template>
                        <template #[`cell(patientPay)`]="data">
                            <div class="text-right">
                                {{ data.value }}
                            </div>
                        </template>
                        <template #[`cell(billingWarning)`]="data">
                            <span v-if="data.item.hasBillingWarning"
                                  class="text-danger"
                                  :title="data.item.billingWarningReason">
                                <b-icon icon="exclamation-triangle-fill" aria-hidden="true"></b-icon>
                                {{ data.item.billingWarningReason }}
                            </span>
                        </template>
                        <template #[`cell(details)`]="data">
                            <!--							{{data.item.rxId}}-->
                            <b-button @click="selectedPrescription = data.item">
                                <b-icon icon="clipboard" aria-hidden="true"></b-icon>
                                details
                            </b-button>

                        </template>
                    </b-table>
                </b-col>
            </b-row>
        </b-card>
        <b-modal v-model="selectedPrescriptionExists"
                 size="xl"
                 title="Prescription Details"
                 hide-footer>
            <prescription-details v-model="selectedPrescription"/>
        </b-modal>
    </b-container>
</template>

<script lang="ts">
import axios from "axios";
import {Component, Ref, Vue} from "vue-facing-decorator";
import {BTable, BvTableCtxObject} from "bootstrap-vue";
import {OrderLabelData} from "@/models/OrderLabelData";
import PrescriptionDetails from "@/components/PrescriptionDetails.vue";
import {Prescription} from "@/models/Prescription";

@Component({
    components: {PrescriptionDetails},
})
export default class DoctorQueue extends Vue {
    @Ref private doctorQueueTable!: BTable;
    private selectedPrescription: Prescription | null = null;
    protected selected: any[] = [];
    private allItems: OrderLabelData[] = [];
    protected submitting: boolean = false;

    protected fields = [
        {key: "selected", label: ""},
        {key: "billingWarning", label: "Billing"},
        {key: "orderId", label: "Id"},
        {key: "ndc", label: "NDC"},
        {key: "productNameShort", label: "Drug"},
        {key: "patientID", label: "Patient", formatter: this.patientFormatter},
        {key: "storeName", label: "Store"},
        {key: "rxId", label: "Rx Id"},
        {key: "dispensed", label: "Dispensed"},
        {key: "fillDate", label: "Date", formatter: this.dateFormatter},
        {key: "patientPay", label: "Patient Pay", formatter: this.currencyFormatter},
        {key: "programName", label: "Program"},
        {key: "orderDate", label: "Order Date", formatter: this.dateFormatter, sortable: true},
        {key: "details", label: ""},
    ];

    get requiredRxIds(): string[] {
        let selectedOrderIds = this.selected.map(i => i.orderId);
        console.log("selectedOrderIds: ", selectedOrderIds);
        let selectedRxIds = this.selected.map(i => i.rxId);
        console.log("selectedRxIds: ", selectedRxIds);
        let items = this.allItems;
        // console.log("items: ", items);

        let requiredRxIds = items.filter(i => selectedOrderIds.includes(i.orderId)).map(i => i.rxId);
        return requiredRxIds;
    }
    get selectedRxIds(): string[] {
        let selectedRxIds = this.selected.map(i => i.rxId);
        console.log("selectedRxIds: ", selectedRxIds);
        return selectedRxIds;
    }
    get neededRxIds(): string[] {
        let neededRxIds = [];
        for (const requiredRxId of this.requiredRxIds) {
            if (!this.selectedRxIds.includes(requiredRxId)){
                neededRxIds.push(requiredRxId);
            }
        }
        return neededRxIds;
    }
    get disableSubmit(): boolean {
        return this.selected.length === 0 || this.submitting || this.neededRxIds.length > 0;
    }

    get selectedPrescriptionExists(): boolean {
        return this.selectedPrescription !== null;
    }

    // used after modal is hidden to reset `selectedPrescription`
    // noinspection JSUnusedGlobalSymbols
    set selectedPrescriptionExists(value: boolean) {
        if (!value) {
            this.selectedPrescription = null;
        }
    }

    async itemProvider(ctx: BvTableCtxObject): Promise<any[] | []> {
        try {
            const url = ctx.apiUrl + "";
            let resp = await axios.get<OrderLabelData[]>(url);
            this.allItems = resp.data;
            return resp.data;
        } catch (err) {
            console.log("err: ", err);
            return [];
        }
    }

    patientFormatter(_value: number, _key: string, item: OrderLabelData): string {
        return `${item.patientLastName}, ${item.patientFirstName}`;
    }

    dateFormatter(value: string, _key: string, _item: OrderLabelData): string {
        if (!value) return "";
        const date = Date.parse(value);
        return Intl.DateTimeFormat().format(date);
    }

    currencyFormatter(value: number, _key: string, _item: OrderLabelData): string {
        if (value === null || isNaN(value)) return "";
        return `$${value.toFixed(2)}`;
    }

    selectAll(): void {
        if (this.selected.length == 0) {
            this.selected = this.allItems;
        } else {
            this.selected = [];
        }
    }

    async submitScripts(): Promise<void> {
        this.submitting = true;
        const rxIds = this.selected.map(i => i.rxId);
        try {
            let resp = await axios.post("api/DoctorQueue/RxIds", rxIds);
            console.log("resp: ", resp);
        } catch (err: any) {
            console.log("err: ", err);
            let message = err?.response?.data?.message;
            if (message.length > 0) alert(err?.response?.data?.message);

        } finally {
            this.submitting = false;
            this.doctorQueueTable.refresh();
            this.selected = [];
        }
    }

    colorCodeTable(rx: OrderLabelData, _type: any): string|undefined {
        if (!rx) return;
        if (rx.hasBillingWarning) return "table-danger";
        if (rx.rxId && this.neededRxIds.length > 0 && this.neededRxIds.includes(rx.rxId)) {
            return "table-warning";
        }
        return;
    }

}
</script>

<style scoped>
.text-right {
    text-align: right;
}
</style>
